import {
  type ImportCustomerContractsRowResult,
  ImportCustomerContractsRowStatus,
} from '@ebalance/proto/bffuser/v1/customer_contract_csv_model_pb'
import {
  ContractStage,
  type PrivateGeneration,
} from '@ebalance/proto/bffuser/v1/customer_model_pb'
import { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'

import { type CivilDate, formatCivilDateSlash } from '~/models/civilDate'
import type { SummaryCardFilter } from '~/pages/CustomerContractCsvUpload/components/SummaryCards'

type Translate = (key: string, options?: Record<string, unknown>) => string

/** プレビュー表の描画上限行数。 */
export const PREVIEW_DISPLAY_LIMIT = 1000

/** 区分の表示名 (顧客管理の契約推移と同じ文言)。 */
export const stageLabel = (t: Translate, stage: ContractStage): string => {
  switch (stage) {
    case ContractStage.CONTRACTED:
      return t('page:customerInfo.stageFilter.contracted')
    case ContractStage.PROSPECT:
      return t('page:customerInfo.stageFilter.prospect')
    case ContractStage.LOST:
      return t('page:customerInfo.stageFilter.lost')
    default:
      return ''
  }
}

/** 電圧階級の表示名 (顧客管理と同じ文言)。 */
export const voltageLabel = (t: Translate, voltage: VoltageClass): string => {
  switch (voltage) {
    case VoltageClass.EXTRA_HIGH:
      return t('page:customerInfo.voltage.extraHigh')
    case VoltageClass.HIGH:
      return t('page:customerInfo.voltage.high')
    case VoltageClass.LOW:
      return t('page:customerInfo.voltage.low')
    default:
      return ''
  }
}

/**
 * 変更の行の列を「変更前 → 変更後」で描く (common.md「変更行は 変更前 → 変更後 をインラインで表示」)。
 * 値が変わらない列、変更でない行は変更後の値だけを出す。変更前が空 (未設定) の列は `unset` を前に置く。
 */
export const transitionText = (
  row: Pick<ImportCustomerContractsRowResult, 'status'>,
  before: string,
  after: string,
  unset: string,
): string => {
  if (
    row.status !== ImportCustomerContractsRowStatus.CHANGED ||
    before === after
  ) {
    return after
  }
  return `${before === '' ? unset : before} → ${after}`
}

/** 自家発の表示 (なし / あり 容量 kW・逆調 kW)。 */
export const privateGenerationText = (
  t: Translate,
  g: PrivateGeneration | undefined,
): string => {
  if (g === undefined || !g.has) {
    return t('page:customerContractCsvUpload.step2.privateGeneration.none')
  }
  const capacity = g.capacityKw?.value ?? ''
  const reverse = g.reversePowerFlowContractKw?.value
  return reverse === undefined
    ? t('page:customerContractCsvUpload.step2.privateGeneration.capacity', {
        capacity,
      })
    : t('page:customerContractCsvUpload.step2.privateGeneration.withReverse', {
        capacity,
        reverse,
      })
}

/** 供給期間の表示 ("開始 〜 終了")。終了日が無い時は終了側を空にして "開始 〜" とする。 */
export const periodText = (
  start: CivilDate | undefined,
  end: CivilDate | undefined,
): string =>
  `${formatCivilDateSlash(start)} 〜 ${formatCivilDateSlash(end)}`.trimEnd()

/** 契約電力の期間の表示 (`2027/04/01 から 400 kW、 2028/04/01 から 350 kW`、無ければ「—」)。 */
export const kwPeriodsText = (
  t: Translate,
  periods: ReadonlyArray<{
    startDate?: CivilDate
    contractKw?: { value: string }
  }>,
): string =>
  periods.length === 0
    ? '—'
    : periods
        .map((p) =>
          t('page:customerInfo.kwPeriod.scheduled', {
            date: formatCivilDateSlash(p.startDate),
            kw: p.contractKw?.value ?? '',
          }),
        )
        .join('、 ')

/** 契約電力の期間を出す行があるか (無いファイルでは列を出さず、表の幅を増やさない)。 */
export const hasKwPeriods = (
  rows: readonly ImportCustomerContractsRowResult[],
): boolean =>
  rows.some((r) => r.kwPeriods.length > 0 || r.existingKwPeriods.length > 0)

/** サマリカードのフィルタ選択を row_results に適用する。 */
export const filterRows = (
  rows: readonly ImportCustomerContractsRowResult[],
  filter: SummaryCardFilter,
): ImportCustomerContractsRowResult[] => {
  const byStatus = (
    status: ImportCustomerContractsRowStatus,
  ): ImportCustomerContractsRowResult[] =>
    rows.filter((r) => r.status === status)
  switch (filter) {
    case 'new':
      return byStatus(ImportCustomerContractsRowStatus.NEW)
    case 'changed':
      return byStatus(ImportCustomerContractsRowStatus.CHANGED)
    case 'error':
      return byStatus(ImportCustomerContractsRowStatus.ERROR)
    case 'unchanged':
      // 据え置き行は row_results に含まれない (件数は summary.unchangedRowCount が SoT)
      return []
    case 'diff':
      // 差分のみ = 新規 + 変更 (据え置き・エラーは含めない)
      return rows.filter(
        (r) =>
          r.status === ImportCustomerContractsRowStatus.NEW ||
          r.status === ImportCustomerContractsRowStatus.CHANGED,
      )
    case 'all':
      return [...rows]
  }
}

/**
 * プレビュー表に描画する行を選ぶ。上限以内は全行そのまま。超過時はエラー行を優先した上で先頭
 * PREVIEW_DISPLAY_LIMIT 行に切り詰める (全行を DOM 化するとブラウザが長時間ブロックするため)。
 */
export const selectPreviewDisplayRows = (
  rows: readonly ImportCustomerContractsRowResult[],
): ImportCustomerContractsRowResult[] => {
  if (rows.length <= PREVIEW_DISPLAY_LIMIT) return [...rows]
  const isError = (r: ImportCustomerContractsRowResult): boolean =>
    r.status === ImportCustomerContractsRowStatus.ERROR
  return [...rows.filter(isError), ...rows.filter((r) => !isError(r))].slice(
    0,
    PREVIEW_DISPLAY_LIMIT,
  )
}
